// Расширение в пути обязательно: модуль грузится не только сборщиком, но и
// node-тестами напрямую (см. tests/gazobeton/commercial_proposal_blocks.test.js).
import { buildGazVehicleLoadView } from './gazVehicleLoadView.js';
import { palletsWord } from '../../utils/gazDeclensions.js';

const isFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const parseDimensionsFromName = (name) => {
  const match = name.match(/(\d+(?:\.\d+)?)х(\d+(?:\.\d+)?)х(\d+(?:\.\d+)?)\s*мм/i);
  if (match) {
    const width = parseFloat(match[1]) / 1000;
    const height = parseFloat(match[2]) / 1000;
    const length = parseFloat(match[3]) / 1000;
    return { width, height, length };
  }
  return null;
};

const getVolumePerUnit = (material) => {
  let volumePerUnit = isFiniteNumber(material.volume);
  if (volumePerUnit > 0) {
    return volumePerUnit;
  }

  const dimensions = parseDimensionsFromName(material.name || '');
  if (dimensions) {
    volumePerUnit = dimensions.width * dimensions.height * dimensions.length;
    if (volumePerUnit > 0) {
      return volumePerUnit;
    }
  }

  return 0;
};

const roundTo3 = (value) => Math.round(isFiniteNumber(value) * 1000) / 1000;

const MANIPULATOR_SERVICE_ONLY_PRODUCTIONS = [
  'кси могилевский',
  'лср, кикерино',
  'могилевский газосиликат',
];

// Манипулятор приезжает на объект пустым и снимает ВЕСЬ заказ, включая то, что
// привезли шаланды. Прежняя подпись «Услуга по работе манипулятора» объёма работ
// не называла, и заказчик читал её как разгрузку одной машины (Роман, 20.10.2026).
// Слово «услуга» сохраняем: товар этой строкой не везётся, это только работа.
const MANIPULATOR_SERVICE_LABEL = 'Услуга манипулятора — разгрузка всего объёма';

/**
 * Завод, чей манипулятор только разгружает: он приезжает на объект без груза,
 * поэтому весь газобетон едет шаландами.
 *
 * Сравнение по вхождению подстроки, как и было. Серверный расчёт держит свой
 * список (BELARUS_MANUFACTURER_NAMES в routes/deliveryGazCalculator.js) и сверяет
 * имя целиком — совпадение двух правил закреплено тестом
 * tests/gazobeton/gaz_manipulator_service_only_lists.test.js.
 */
export const isManipulatorServiceOnlyProduction = (productionName) => {
  const name = String(productionName ?? '').trim().toLowerCase();
  return MANIPULATOR_SERVICE_ONLY_PRODUCTIONS.some((candidate) => name.includes(candidate));
};

/**
 * Паллетоместа сопутствующей позиции для МСК Газобетона.
 *
 * Штучный товар (U-блок, клей) переводится в поддоны через «штук на поддоне» с
 * округлением вверх: завод отгружает только целыми поддонами. Делить штуки на
 * `palletUnits` нельзя — это кубы на поддоне, а не штуки.
 *
 * null — норму взять неоткуда. Придумывать её нельзя: позиция уходит в
 * unmeasuredCargoNames, и свободное место в такой машине не обещается.
 */
export const getUpsellPalletPlacesForQuantity = (material, quantity) => {
  const qty = isFiniteNumber(quantity);
  if (!(qty > 0)) {
    return null;
  }

  const unitsPerPallet = isFiniteNumber(material?.unitsPerPallet);
  if (unitsPerPallet > 0) {
    return Math.ceil(qty / unitsPerPallet);
  }

  // Позиция в м3 — только там palletUnits действительно объём поддона.
  const palletUnits = isFiniteNumber(material?.palletUnits);
  if (palletUnits > 0 && String(material?.unit || '').trim().toLowerCase() === 'м3') {
    return Math.ceil(qty / palletUnits);
  }

  return null;
};

export const getUpsellPalletPlaces = (material) => {
  const directPallets = isFiniteNumber(material?.numberPallets);
  if (directPallets > 0) {
    return Math.ceil(directPallets);
  }

  const occupiedPallets = isFiniteNumber(material?.occupiedPallets);
  if (occupiedPallets > 0) {
    return Math.ceil(occupiedPallets);
  }

  return getUpsellPalletPlacesForQuantity(material, material?.quantity);
};

const getMaterialPalletCount = (material) => {
  const directPallets = isFiniteNumber(material?.numberPallets);
  if (directPallets > 0) {
    return directPallets;
  }

  const occupiedPallets = isFiniteNumber(material?.occupiedPallets);
  if (occupiedPallets > 0) {
    return occupiedPallets;
  }

  const totalPallets = isFiniteNumber(material?.totalPallets);
  if (totalPallets > 0) {
    return totalPallets;
  }

  const quantity = isFiniteNumber(material?.quantity);
  const palletUnits = isFiniteNumber(material?.palletUnits);
  if (quantity > 0 && palletUnits > 0) {
    return roundTo3(quantity / palletUnits);
  }

  return 0;
};

// Объём (м3) на один поддон конкретного материала. У разных толщин блока (и у разных
// производств) этот объём разный, поэтому поддоны разных материалов нельзя напрямую
// складывать друг с другом при сравнении с нормой погрузки шаланды/манипулятора.
const getMaterialPalletVolume = (material, materialPallets) => {
  const declared = isFiniteNumber(material?.palletUnits);
  if (declared > 0) {
    return declared;
  }

  const quantity = isFiniteNumber(material?.factVolume ?? material?.quantity);
  return materialPallets > 0 && quantity > 0 ? quantity / materialPallets : 0;
};

// Приводит поддоны материала к эквиваленту в норме доминирующего по объёму материала
// заказа (той же норме, что бэкенд использует для выбора shalandaCapacity), чтобы
// суммарное число поддонов можно было корректно сравнивать с ней.
const getNormalizedPalletCount = (material, dominantPalletVolume) => {
  const pallets = getMaterialPalletCount(material);
  if (pallets <= 0) {
    return 0;
  }
  if (!(dominantPalletVolume > 0)) {
    return pallets;
  }

  const materialPalletVolume = getMaterialPalletVolume(material, pallets);
  if (!(materialPalletVolume > 0)) {
    return pallets;
  }

  return (pallets * materialPalletVolume) / dominantPalletVolume;
};

const isGazobetonExtraMaterial = (material) => {
  const category = String(material?.category || '').toLowerCase();
  const name = String(material?.name || '').toLowerCase();

  return (
    category.includes('клей')
    || category.includes('u-блок')
    || category.includes('добавка')
    || name.includes('клей')
    || name.includes('u-блок')
    || name.includes('добавка')
  );
};

const isGazobetonMaterial = (material) => {
  const calculatorCategory = String(material?.calculatorCategory || '').toLowerCase();
  const category = String(material?.category || '').toLowerCase();
  const name = String(material?.name || '').toLowerCase();

  if (isGazobetonExtraMaterial(material)) {
    return false;
  }

  const gazDeliveryCategoryNames = new Set([
    'бордюр',
    'бордюр дорожный',
    'брусчатка',
    'брусчатка и тротуарная плитка',
    'плитка тротуарная',
    'газонная решетка',
    'лоток водоотводный жби',
    'лоток водоотводный тротуарный',
  ]);
  const isGazDeliveryCategory = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized.includes('газобетон') || gazDeliveryCategoryNames.has(normalized);
  };

  return isGazDeliveryCategory(calculatorCategory) || isGazDeliveryCategory(category) || name.includes('газобетон');
};

const getOfferProductionName = (offer, materials, breakdown) => {
  const candidates = [
    offer?.productionInfo?.NAME,
    breakdown?.manufacturer,
    ...materials.map((material) => material?.manufacturer || material?.production),
  ];

  return candidates.find((value) => String(value || '').trim()) || '';
};

const buildVolumeShares = (materials, totalVolume) => {
  if (totalVolume <= 0) {
    return materials.map(() => 0);
  }

  const rawShares = materials.map((material) => {
    const volume = isFiniteNumber(material.factVolume ?? material.quantity);
    return volume > 0 ? volume / totalVolume : 0;
  });

  const totalShares = rawShares.reduce((sum, value) => sum + value, 0);
  if (totalShares <= 0) {
    return materials.map(() => 0);
  }

  return rawShares.map((value) => value / totalShares);
};

const allocateByShares = (totalValue, shares) => {
  let allocated = 0;
  return shares.map((share, index) => {
    if (index === shares.length - 1) {
      return roundTo3(totalValue - allocated);
    }
    const value = roundTo3(totalValue * share);
    allocated += value;
    return value;
  });
};

// Распределяет объём материалов между "основным" (полные шаланды) и "остаточным" блоком,
// двигаясь по заказу материал за материалом и заполняя бюджет основного блока в м3.
// Считаем именно по объёму (а не по счётчику поддонов): у материалов заказа может быть
// разный объём на поддон, а м3 — единственная единица, одинаковая для всех позиций.
const allocateVolumesByPalletSequence = ({ materials, mainVolume: mainVolumeBudget, dominantPalletVolume }) => {
  let remainingMainVolume = Math.max(isFiniteNumber(mainVolumeBudget), 0);

  const allocation = materials.map((material) => {
    const totalVolume = isFiniteNumber(material.factVolume ?? material.quantity);

    if (totalVolume <= 0) {
      return { mainVolume: 0, secondaryVolume: 0 };
    }

    const mainVolume = roundTo3(Math.min(remainingMainVolume, totalVolume));
    remainingMainVolume = Math.max(roundTo3(remainingMainVolume - mainVolume), 0);

    return {
      mainVolume,
      secondaryVolume: roundTo3(totalVolume - mainVolume),
    };
  });

  const toPallets = (volume) => (dominantPalletVolume > 0 ? roundTo3(volume / dominantPalletVolume) : 0);

  return {
    mainPallets: allocation.map((row) => toPallets(row.mainVolume)),
    secondaryPallets: allocation.map((row) => toPallets(row.secondaryVolume)),
    mainVolumes: allocation.map((row) => row.mainVolume),
    secondaryVolumes: allocation.map((row) => row.secondaryVolume),
  };
};

const buildPriceSummaries = ({ materials, volumes, blockTotal }) => {
  const totalVolume = volumes.reduce((sum, value) => sum + value, 0);

  if (!materials.length || totalVolume <= 0 || blockTotal <= 0) {
    return [];
  }

  const shares = buildVolumeShares(
    materials.map((material, index) => ({ ...material, quantity: volumes[index], factVolume: volumes[index] })),
    totalVolume
  );
  const allocatedSums = allocateByShares(blockTotal, shares);

  return materials.map((material, index) => {
    const volume = volumes[index];
    const allocatedSum = allocatedSums[index];
    const volumePerUnit = getVolumePerUnit(material);
    const pieces = volumePerUnit > 0 ? roundTo3(volume / volumePerUnit) : 0;
    return {
      materialName: material.name,
      volume: roundTo3(volume),
      allocatedSum,
      pricePerM3: volume > 0 ? roundTo3(allocatedSum / volume) : 0,
      pieces,
      unit: material.unit || 'м3',
    };
  }).filter((row) => row.volume > 0);
};

const createMaterialRows = (materials, volumes) => {
  return materials.map((material, index) => {
    const quantity = roundTo3(volumes[index]);
    const pricePerOne = isFiniteNumber(material.pricePerOne);
    const volumePerUnit = getVolumePerUnit(material);
    const pieces = volumePerUnit > 0 ? roundTo3(quantity / volumePerUnit) : 0;

    return {
      key: `material-${index}-${material.name}`,
      kind: 'material',
      sourceIndex: index,
      name: material.name,
      quantity,
      unit: material.unit || 'м3',
      pricePerOne,
      sum: roundTo3(pricePerOne * quantity),
      originalIndex: material.__sourceIndex,
      pieces,
      title: pieces > 0 ? `${pieces} шт.` : undefined,
    };
  }).filter((row) => row.quantity > 0);
};

const createExtraMaterialRows = (materials) => {
  return materials.map((material, index) => {
    const quantity = roundTo3(isFiniteNumber(material.quantity));
    const pricePerOne = isFiniteNumber(material.pricePerOne);
    const volumePerUnit = getVolumePerUnit(material);
    const pieces = volumePerUnit > 0 ? roundTo3(quantity / volumePerUnit) : 0;

    return {
      key: `extra-material-${index}-${material.name}`,
      kind: 'material',
      sourceIndex: index,
      name: material.name,
      quantity,
      unit: material.unit || 'шт',
      pricePerOne,
      sum: roundTo3(pricePerOne * quantity),
      originalIndex: material.__sourceIndex,
      pieces,
      title: pieces > 0 ? `${pieces} шт.` : undefined,
    };
  }).filter((row) => row.quantity > 0);
};

// Состав газобетонного груза блока для карточек машин: имя, объём, поддоны, штуки.
// Ничего не считает — берёт уже разложенные по блокам объёмы (mainVolumes /
// secondaryVolumes) и уже посчитанные поддоны (mainGazPallets / secondaryGazPallets).
// `row.sourceIndex` ставится до фильтрации пустых строк, поэтому по нему безопасно
// возвращаться к позиции материала.
const buildGazCargoItems = (rows, palletsByMaterial, materials) => rows
  // Только строки основного газобетона. Ключ `extra-material-*` — это сопутствующая
  // позиция, её `sourceIndex` считается по другому массиву, и мешать их нельзя.
  .filter((row) => row.kind === 'material' && String(row.key || '').startsWith('material-'))
  // Добавленные допродажи сюда не попадают, даже если по категории они прошли как
  // газобетон: их строку с редактируемым количеством, поддономестами и стоимостью
  // рисует блок допродаж. Иначе позиция удвоилась бы и в списке, и в поддонах.
  .filter((row) => !materials?.[row.sourceIndex]?.isFromInsulationFriends)
  .map((row) => {
    const pallets = isFiniteNumber(palletsByMaterial?.[row.sourceIndex]);
    const pieces = isFiniteNumber(row.pieces);
    return {
      name: row.name,
      volume: roundTo3(row.quantity),
      unit: row.unit,
      // null — норму поддона взять неоткуда; выдумывать её нельзя.
      pallets: pallets > 0 ? roundTo3(pallets) : null,
      // Штуки показываем, только если они достоверно посчитаны.
      pieces: pieces > 0 ? pieces : null,
    };
  });

const createServiceRow = ({ key, name, quantity, unit, pricePerOne, sum }) => ({
  key,
  kind: 'service',
  name,
  quantity: roundTo3(quantity),
  unit,
  pricePerOne: roundTo3(pricePerOne),
  sum: roundTo3(sum),
});

const buildSummaryBaseTotal = ({ rows, gazOriginalIndexes }) => (
  roundTo3(rows.reduce((sum, row) => {
    if (row.kind === 'material' && gazOriginalIndexes.has(row.originalIndex)) {
      return sum + isFiniteNumber(row.sum);
    }

    return sum;
  }, 0))
);

const buildGazSummaryServiceTotal = ({
  gazPallets,
  shalandaCapacity,
  shalandaTripCost,
  manipulatorGoesWithShalands,
  manipulatorTripCost,
  unloadingCost,
  craneUnloadingCost,
}) => {
  const gasPalletCount = Math.max(isFiniteNumber(gazPallets), 0);
  if (gasPalletCount <= 0) {
    return 0;
  }

  if (manipulatorGoesWithShalands) {
    return roundTo3(manipulatorTripCost + (unloadingCost > 0 ? unloadingCost : 0));
  }

  const shalandaTripsForGaz = shalandaCapacity > 0
    ? Math.ceil(gasPalletCount / shalandaCapacity)
    : 0;

  let total = roundTo3(shalandaTripsForGaz * shalandaTripCost);

  if (unloadingCost > 0) {
    total += unloadingCost;
  }

  if (craneUnloadingCost > 0) {
    total += craneUnloadingCost;
  }

  return roundTo3(total);
};

const distributeTripsByPallets = ({ totalTrips, mainPallets, secondaryPallets }) => {
  const trips = Math.max(isFiniteNumber(totalTrips), 0);
  const main = Math.max(isFiniteNumber(mainPallets), 0);
  const secondary = Math.max(isFiniteNumber(secondaryPallets), 0);
  const total = main + secondary;

  if (trips <= 0 || total <= 0) {
    return { mainTrips: 0, secondaryTrips: 0 };
  }
  if (main <= 0) {
    return { mainTrips: 0, secondaryTrips: trips };
  }
  if (secondary <= 0) {
    return { mainTrips: trips, secondaryTrips: 0 };
  }

  let mainTrips = Math.floor((trips * main) / total);
  let secondaryTrips = trips - mainTrips;

  if (mainTrips <= 0) {
    mainTrips = 1;
    secondaryTrips = Math.max(trips - 1, 0);
  }
  if (secondaryTrips <= 0) {
    secondaryTrips = 1;
    mainTrips = Math.max(trips - 1, 0);
  }

  return { mainTrips, secondaryTrips };
};

/**
 * @param {object} offer
 * @param {object} [options]
 * @param {boolean} [options.isMskGazobetonScope] — признак области приходит готовым
 *   сверху (OfferTable). Здесь он не вычисляется. Вне области подпись блока остатка
 *   собирается ровно как в 5442898.
 */
export const buildGazCommercialProposalBlocks = (offer, { isMskGazobetonScope = false } = {}) => {
  const materials = Array.isArray(offer?.materials) ? offer.materials : [];
  const indexedMaterials = materials.map((material, index) => ({ ...material, __sourceIndex: index }));
  const gazMaterials = indexedMaterials.filter(isGazobetonMaterial);
  const extraMaterials = indexedMaterials.filter((material) => !isGazobetonMaterial(material));

  if (!gazMaterials.length) {
    return { isGazobetonOffer: false, blocks: [] };
  }

  const primaryMaterial = gazMaterials[0];
  const categoryLabel = primaryMaterial?.category || 'Газобетон';
  const primaryUnit = primaryMaterial?.unit || 'м3';

  const breakdown = offer?.delivery?.deliveryBreakdown || {};
  const unloadMethod = String(breakdown.unloadMethod || '').toLowerCase();
  const productionName = String(getOfferProductionName(offer, indexedMaterials, breakdown)).toLowerCase();
  const forceShalandaWithManipulatorService = isManipulatorServiceOnlyProduction(productionName);
  const shalandaTripCost = isFiniteNumber(breakdown.shalandaTripCost ?? offer?.delivery?.costPerTrip);
  const shalandaCount = isFiniteNumber(breakdown.shalandaCount ?? offer?.delivery?.numberOfTrips);
  const fullShalandaCount = isFiniteNumber(breakdown.fullShalandaCount);
  const remainderPallets = isFiniteNumber(breakdown.remainderPallets);
  const shalandaCapacity = isFiniteNumber(breakdown.shalandaCapacity);
  const backendDominantPalletVolume = isFiniteNumber(breakdown.dominantPalletVolume);
  const forcedManipulatorDelivery = breakdown.forcedManipulatorDelivery === true;
  const manipulatorGoesWithShalands = breakdown.manipulatorGoesWithShalands === true;
  const canUseManipulatorDelivery = manipulatorGoesWithShalands && (!forceShalandaWithManipulatorService || shalandaCount === 0);
  const missingManipulatorCapacityForUnloading = breakdown.missingManipulatorCapacityForUnloading === true;
  const manipulatorDeliveryPallets = isFiniteNumber(breakdown.manipulatorDeliveryPallets);
  const manipulatorDeliveryTrips = isFiniteNumber(breakdown.manipulatorDeliveryTrips);
  const shalandaPallets = isFiniteNumber(breakdown.shalandaPallets);
  const manipulatorTripCost = isFiniteNumber(breakdown.manipulatorTripCost);
  const manipulatorTripCostPerTrip = isFiniteNumber(breakdown.manipulatorTripCostPerTrip);
  const unloadingCost = isFiniteNumber(breakdown.unloadingCost);
  const craneUnloadingCost = isFiniteNumber(breakdown.craneUnloadingCost);

  const totalGazVolume = gazMaterials.reduce((sum, material) => sum + isFiniteNumber(material.factVolume ?? material.quantity), 0);
  const rawTotalGazPallets = gazMaterials.reduce((sum, material) => sum + getMaterialPalletCount(material), 0);
  const hasPalletAllocationData = rawTotalGazPallets > 0 && gazMaterials.every((material) => getMaterialPalletCount(material) > 0);
  // Норма объёма на поддон доминирующей позиции (та же, что бэкенд использовал для выбора
  // shalandaCapacity). Если бэкенд её не прислал (старый кэш ответа) — берём средний объём
  // на поддон по всей группе газобетона как безопасный запасной вариант.
  const dominantPalletVolume = backendDominantPalletVolume > 0
    ? backendDominantPalletVolume
    : (rawTotalGazPallets > 0 ? totalGazVolume / rawTotalGazPallets : 0);
  // Поддоны разных позиций (толщин блока) физически различаются по объёму, поэтому для
  // сравнения с нормой погрузки (shalandaCapacity, заданной для доминирующей позиции)
  // приводим количество поддонов каждой позиции к этой же норме.
  const totalGazPallets = gazMaterials.reduce(
    (sum, material) => sum + getNormalizedPalletCount(material, dominantPalletVolume),
    0
  );
  const displayGazFullShalandaCount = shalandaCapacity > 0
    ? (forcedManipulatorDelivery ? shalandaCount : (canUseManipulatorDelivery && shalandaCount > 0 ? shalandaCount : Math.floor(totalGazPallets / shalandaCapacity)))
    : fullShalandaCount;
  const displayGazRemainderPallets = shalandaCapacity > 0
    ? (canUseManipulatorDelivery
      ? (manipulatorDeliveryPallets > 0 ? manipulatorDeliveryPallets : remainderPallets)
      : Math.max(totalGazPallets - (displayGazFullShalandaCount * shalandaCapacity), 0))
    : remainderPallets;

  let mainVolumes = [];
  let secondaryVolumes = [];
  let mainGazPallets = [];
  let secondaryGazPallets = [];

  if (hasPalletAllocationData) {
    const mainPallets = canUseManipulatorDelivery
      ? (forcedManipulatorDelivery ? shalandaPallets : (shalandaPallets > 0 ? shalandaPallets : Math.max(totalGazPallets - displayGazRemainderPallets, 0)))
      : (displayGazFullShalandaCount > 0 && shalandaCapacity > 0
        ? displayGazFullShalandaCount * shalandaCapacity
        : Math.max(totalGazPallets - displayGazRemainderPallets, 0));
    const cappedMainPallets = Math.min(mainPallets, totalGazPallets);
    const mainVolumeBudget = dominantPalletVolume > 0
      ? cappedMainPallets * dominantPalletVolume
      : totalGazVolume;
    const palletAllocation = allocateVolumesByPalletSequence({
      materials: gazMaterials,
      mainVolume: mainVolumeBudget,
      dominantPalletVolume,
    });
    mainGazPallets = palletAllocation.mainPallets;
    secondaryGazPallets = palletAllocation.secondaryPallets;
    mainVolumes = palletAllocation.mainVolumes;
    secondaryVolumes = palletAllocation.secondaryVolumes;
  } else {
    const materialPalletShares = buildVolumeShares(gazMaterials, totalGazVolume);
    const remainderVolumeByShares = totalGazPallets > 0
      ? roundTo3(totalGazVolume * (displayGazRemainderPallets / totalGazPallets))
      : 0;
    const mainVolumeByShares = roundTo3(Math.max(totalGazVolume - remainderVolumeByShares, 0));
    mainVolumes = allocateByShares(mainVolumeByShares, materialPalletShares);
    secondaryVolumes = allocateByShares(remainderVolumeByShares, materialPalletShares);
    mainGazPallets = gazMaterials.map((material, index) => {
      const totalPallets = getNormalizedPalletCount(material, dominantPalletVolume);
      const totalVolume = isFiniteNumber(material.factVolume ?? material.quantity);
      const mainVolume = isFiniteNumber(mainVolumes[index]);
      return totalVolume > 0 ? roundTo3(totalPallets * (mainVolume / totalVolume)) : 0;
    });
    secondaryGazPallets = gazMaterials.map((material, index) => {
      const totalPallets = getNormalizedPalletCount(material, dominantPalletVolume);
      const totalVolume = isFiniteNumber(material.factVolume ?? material.quantity);
      const secondaryVolume = isFiniteNumber(secondaryVolumes[index]);
      return totalVolume > 0 ? roundTo3(totalPallets * (secondaryVolume / totalVolume)) : 0;
    });
  }

  const mainVolume = roundTo3(mainVolumes.reduce((sum, value) => sum + value, 0));
  const remainderVolume = roundTo3(secondaryVolumes.reduce((sum, value) => sum + value, 0));

  const mainRows = createMaterialRows(gazMaterials, mainVolumes);
  const secondaryRows = createMaterialRows(gazMaterials, secondaryVolumes);
  const extraMaterialRows = createExtraMaterialRows(extraMaterials);
  const hasMainCargo = mainRows.some((row) => row.kind === 'material');
  const hasSecondaryCargo = secondaryRows.some((row) => row.kind === 'material') || extraMaterialRows.length > 0;
  const secondaryExtraPallets = extraMaterials.reduce((sum, material) => sum + getMaterialPalletCount(material), 0);
  const mainCargoPallets = mainGazPallets.reduce((sum, value) => sum + isFiniteNumber(value), 0);
  const secondaryCargoPallets = secondaryGazPallets.reduce((sum, value) => sum + isFiniteNumber(value), 0) + secondaryExtraPallets;

  // Допродажи добавляются во второй блок отдельными строками и не участвуют
  // в текущем расчете раскладки по поддонам.
  extraMaterialRows.forEach((row) => {
    secondaryRows.push(row);
  });

  let mainShalandaCount = 0;
  let secondaryShalandaCount = 0;

  if (isMskGazobetonScope && forceShalandaWithManipulatorService && shalandaCount > 0) {
    // Число машин берётся у калькулятора и здесь только раскладывается между
    // блоками. Своего деления «поддоны ÷ норма» тут быть не должно: норма в
    // карточке одна — той позиции, которой в заказе больше всего, — а везут
    // несколько толщин, у каждой своя норма погрузки. Пересчёт по одной норме
    // давал лишние рейсы: 84 поддона четырёх толщин превращались в 7 шаланд
    // вместо посчитанных пяти, и КП дорожало на две машины, хотя разгрузка в
    // том же КП оплачивалась за пять.
    const distribution = distributeTripsByPallets({
      totalTrips: shalandaCount,
      mainPallets: hasMainCargo ? mainCargoPallets : 0,
      secondaryPallets: hasSecondaryCargo ? secondaryCargoPallets : 0,
    });
    mainShalandaCount = distribution.mainTrips;
    secondaryShalandaCount = distribution.secondaryTrips;
  } else if (forceShalandaWithManipulatorService && shalandaCapacity > 0 && shalandaCount > 0) {
    // Вне области — прежний путь 5442898, здесь ничего не меняется.
    mainShalandaCount = hasMainCargo ? Math.ceil(mainCargoPallets / shalandaCapacity) : 0;
    secondaryShalandaCount = hasSecondaryCargo ? Math.ceil(secondaryCargoPallets / shalandaCapacity) : 0;
  } else if (canUseManipulatorDelivery && shalandaCount > 0) {
    mainShalandaCount = hasMainCargo ? shalandaCount : 0;
  } else if (canUseManipulatorDelivery && hasMainCargo && shalandaCapacity > 0) {
    mainShalandaCount = Math.ceil(mainCargoPallets / shalandaCapacity);
  } else if (!canUseManipulatorDelivery && shalandaCount > 0) {
    const distribution = distributeTripsByPallets({
      totalTrips: shalandaCount,
      mainPallets: hasMainCargo ? mainCargoPallets : 0,
      secondaryPallets: hasSecondaryCargo ? secondaryCargoPallets : 0,
    });
    mainShalandaCount = distribution.mainTrips;
    secondaryShalandaCount = distribution.secondaryTrips;
  }

  const mainShalandaDeliveryCost = roundTo3(mainShalandaCount * shalandaTripCost);
  const secondaryShalandaDeliveryCost = roundTo3(secondaryShalandaCount * shalandaTripCost);

  if (mainShalandaCount > 0 && mainShalandaDeliveryCost > 0) {
    mainRows.push(
      createServiceRow({
        key: 'main-shalanda',
        name: 'Доставка шаландой',
        quantity: mainShalandaCount,
        unit: 'рейс',
        pricePerOne: shalandaTripCost,
        sum: mainShalandaDeliveryCost,
      })
    );
  }

  if (secondaryShalandaCount > 0 && secondaryShalandaDeliveryCost > 0) {
    secondaryRows.push(
      createServiceRow({
        key: 'secondary-shalanda',
        name: 'Доставка шаландой',
        quantity: secondaryShalandaCount,
        unit: 'рейс',
        pricePerOne: shalandaTripCost,
        sum: secondaryShalandaDeliveryCost,
      })
    );
  }

  if (displayGazRemainderPallets > 0 && canUseManipulatorDelivery && manipulatorTripCost > 0) {
    const manipulatorTripQuantity = manipulatorDeliveryTrips > 0 ? manipulatorDeliveryTrips : 1;
    const tripCostPerOne = manipulatorTripCostPerTrip || (
      manipulatorTripQuantity > 0 ? roundTo3(manipulatorTripCost / manipulatorTripQuantity) : manipulatorTripCost
    );

    if (forcedManipulatorDelivery && unloadingCost > 0) {
      const unloadingTripTotal = roundTo3(tripCostPerOne + unloadingCost);
      const deliveryOnlyTrips = Math.max(manipulatorTripQuantity - 1, 0);
      if (deliveryOnlyTrips > 0) {
        secondaryRows.push(
          createServiceRow({
            key: 'secondary-manipulator-trip-no-unload',
            name: 'Доставка манипулятором без разгрузки',
            quantity: deliveryOnlyTrips,
            unit: 'рейс',
            pricePerOne: tripCostPerOne,
            sum: roundTo3(deliveryOnlyTrips * tripCostPerOne),
          })
        );
      }

      secondaryRows.push(
        createServiceRow({
          key: 'secondary-manipulator-trip',
          name: 'Доставка манипулятором с разгрузкой',
          quantity: 1,
          unit: 'рейс',
          pricePerOne: unloadingTripTotal,
          sum: unloadingTripTotal,
        })
      );
    } else {
      const manipulatorDeliveryTotal = roundTo3(manipulatorTripCost + (unloadingCost > 0 ? unloadingCost : 0));
      secondaryRows.push(
        createServiceRow({
          key: 'secondary-manipulator-trip',
          name: unloadingCost > 0 ? 'Доставка манипулятором с разгрузкой' : 'Манипулятор 10тн',
          quantity: manipulatorTripQuantity,
          unit: 'рейс',
          pricePerOne: manipulatorTripQuantity > 0
            ? roundTo3(manipulatorDeliveryTotal / manipulatorTripQuantity)
            : (manipulatorTripCostPerTrip || manipulatorDeliveryTotal),
          sum: manipulatorDeliveryTotal,
        })
      );
    }
  }

  if (unloadMethod.includes('манипулятор') && unloadingCost > 0 && !canUseManipulatorDelivery) {
    secondaryRows.push(
      createServiceRow({
        key: 'secondary-manipulator-service',
        name: forceShalandaWithManipulatorService
          ? MANIPULATOR_SERVICE_LABEL
          : (missingManipulatorCapacityForUnloading
            ? 'Доставка манипулятором с разгрузкой'
            : MANIPULATOR_SERVICE_LABEL),
        quantity: 1,
        unit: 'услуга',
        pricePerOne: unloadingCost,
        sum: unloadingCost,
      })
    );
  }

  if (unloadMethod.includes('кран') && craneUnloadingCost > 0) {
    secondaryRows.push(
      createServiceRow({
        key: 'secondary-crane-service',
        name: 'Услуги автокрана',
        quantity: 1,
        unit: 'услуга',
        pricePerOne: craneUnloadingCost,
        sum: craneUnloadingCost,
      })
    );
  }

  const mainBlockTotal = roundTo3(mainRows.reduce((sum, row) => sum + isFiniteNumber(row.sum), 0));
  const secondaryBlockTotal = roundTo3(secondaryRows.reduce((sum, row) => sum + isFiniteNumber(row.sum), 0));
  const gazOriginalIndexes = new Set(gazMaterials.map((material) => material.__sourceIndex));
  const mainMaterialSummaryTotal = buildSummaryBaseTotal({ rows: mainRows, gazOriginalIndexes });
  const secondaryMaterialSummaryTotal = buildSummaryBaseTotal({ rows: secondaryRows, gazOriginalIndexes });
  const mainGazSummaryServiceTotal = buildGazSummaryServiceTotal({
    gazPallets: mainCargoPallets,
    shalandaCapacity,
    shalandaTripCost,
    manipulatorGoesWithShalands: false,
    manipulatorTripCost: 0,
    unloadingCost: 0,
    craneUnloadingCost: 0,
  });
  const secondaryGazSummaryServiceTotal = buildGazSummaryServiceTotal({
    gazPallets: secondaryGazPallets.reduce((sum, value) => sum + isFiniteNumber(value), 0),
    shalandaCapacity,
    shalandaTripCost,
    manipulatorGoesWithShalands: canUseManipulatorDelivery,
    manipulatorTripCost,
    unloadingCost,
    craneUnloadingCost,
  });
  const mainSummaryTotal = roundTo3(mainMaterialSummaryTotal + mainGazSummaryServiceTotal);
  const secondarySummaryTotal = roundTo3(secondaryMaterialSummaryTotal + secondaryGazSummaryServiceTotal);
  const secondaryGazLoadedPallets = roundTo3(secondaryGazPallets.reduce((sum, value) => sum + isFiniteNumber(value), 0));
  const manipulatorCapacityPallets = isFiniteNumber(breakdown.manipulatorCapacity);
  const manipulatorTripsForCapacity = manipulatorDeliveryTrips > 0 ? manipulatorDeliveryTrips : 1;

  // Позиции допродаж, для которых паллетоместо посчитать нечем. Их читает модель
  // машин: в такой машине свободная вместимость не обещается.
  const unmeasuredCargoNames = extraMaterials
    .filter((material) => (
      isFiniteNumber(material?.quantity) > 0 && getUpsellPalletPlaces(material) === null
    ))
    .map((material) => String(material?.name || '').trim())
    .filter(Boolean);

  // Внутри МСК Газобетона занятость и свободное место берутся из тех же чисел, что
  // и карточки машин: занятое — серверный remainderPallets (он уже учитывает все
  // позиции заказа, включая добавленные допродажи), вместимость — норма из расчёта.
  // Своего пересчёта из объёмов здесь больше нет: он не видел допродаж, давал
  // дробные поддоны и расходился с карточкой.
  // Машина с остатком строится по тому же условию, что и карточка: нет остатка —
  // нет и неполной машины, а значит свободного места обещать нечего.
  const scopedRemainderPallets = isFiniteNumber(breakdown.remainderPallets);
  const scopedRemainderView = isMskGazobetonScope && scopedRemainderPallets > 0
    ? buildGazVehicleLoadView({
      capacity: secondaryShalandaCount > 0 && shalandaCapacity > 0
        ? shalandaCapacity
        : (canUseManipulatorDelivery && manipulatorCapacityPallets > 0 ? manipulatorCapacityPallets : 0),
      loadedPallets: scopedRemainderPallets,
      palletVolume: isFiniteNumber(breakdown.dominantPalletVolume) || 0,
      hasUnmeasuredCargo: unmeasuredCargoNames.length > 0,
    })
    : null;

  // Базовый путь 5442898 — вне области он не меняется.
  const secondaryLoadedPallets = secondaryGazLoadedPallets;
  const secondaryCapacityPallets = secondaryShalandaCount > 0 && shalandaCapacity > 0
    ? roundTo3(secondaryShalandaCount * shalandaCapacity)
    : (canUseManipulatorDelivery && manipulatorCapacityPallets > 0 && secondaryLoadedPallets > 0
      ? roundTo3(manipulatorCapacityPallets * manipulatorTripsForCapacity)
      : 0);
  const baseSecondaryFreePallets = secondaryCapacityPallets > 0
    ? roundTo3(Math.max(secondaryCapacityPallets - secondaryLoadedPallets, 0))
    : 0;
  const secondaryVolumePerPallet = secondaryLoadedPallets > 0
    ? roundTo3(remainderVolume / secondaryLoadedPallets)
    : 0;
  const baseSecondaryFreeVolume = baseSecondaryFreePallets > 0 && secondaryVolumePerPallet > 0
    ? roundTo3(baseSecondaryFreePallets * secondaryVolumePerPallet)
    : 0;

  const secondaryFreePallets = isMskGazobetonScope
    ? (scopedRemainderView ? scopedRemainderView.freePallets : 0)
    : baseSecondaryFreePallets;
  const secondaryFreeVolume = isMskGazobetonScope
    ? (scopedRemainderView ? (scopedRemainderView.freeVolume || 0) : 0)
    : baseSecondaryFreeVolume;

  const mainBlock = {
    key: 'main',
    title: 'Основной блок',
    subtitle: mainVolume > 0
      ? `${categoryLabel}: ${roundTo3(mainVolume)} ${primaryUnit}${mainShalandaCount > 0 ? ` | Полных шаланд: ${mainShalandaCount}` : ''}${mainRows.some(row => row.kind === 'material' && row.pieces > 0) ? ` | ${mainRows.filter(row => row.kind === 'material').reduce((sum, row) => sum + (row.pieces || 0), 0)} шт.` : ''}`
      : '',
    emptyText: 'Полные шаланды в этом сценарии не формируются.',
    rows: mainRows,
    total: mainBlockTotal,
    // Состав груза для карточек машин. Добавляется только внутри области: вне её
    // объект блока обязан совпадать с 5442898 вплоть до состава ключей.
    ...(isMskGazobetonScope ? { gazCargoItems: buildGazCargoItems(mainRows, mainGazPallets, gazMaterials) } : {}),
    priceSummaries: buildPriceSummaries({
      materials: gazMaterials.filter((_, index) => mainVolumes[index] > 0),
      volumes: mainVolumes.filter((value) => value > 0),
      blockTotal: mainSummaryTotal,
    }),
  };

  const secondarySummaryTextParts = [];
  if (remainderVolume > 0) {
    if (isMskGazobetonScope) {
      // Поддоны остатка — целые, из расчёта доставки: свой пересчёт из объёмов давал
      // дробные значения и расходился с карточкой машины на поддон.
      const remainderPalletsLabel = isFiniteNumber(breakdown.remainderPallets) > 0
        ? isFiniteNumber(breakdown.remainderPallets)
        : (secondaryGazLoadedPallets || displayGazRemainderPallets);
      secondarySummaryTextParts.push(
        `Остаток газобетона: ${roundTo3(remainderVolume)} ${primaryUnit}`
        + ` (${remainderPalletsLabel} ${palletsWord(remainderPalletsLabel)})`
      );
    } else {
      secondarySummaryTextParts.push(`Остаток: ${roundTo3(remainderVolume)} ${primaryUnit} (Остаток поддонов: ${secondaryLoadedPallets || displayGazRemainderPallets})`);
    }
  }
  if (secondaryFreePallets > 0) {
    secondarySummaryTextParts.push(
      `Свободно: ${roundTo3(secondaryFreeVolume)} ${primaryUnit}`
      + ` (${secondaryFreePallets} ${isMskGazobetonScope ? palletsWord(secondaryFreePallets) : 'поддонов'})`
    );
  }
  if (isMskGazobetonScope && unmeasuredCargoNames.length > 0) {
    secondarySummaryTextParts.push(
      `Место не учтено (нет нормы поддона): ${unmeasuredCargoNames.join(', ')}`
    );
  }
  if (!remainderVolume && (unloadingCost > 0 || craneUnloadingCost > 0)) secondarySummaryTextParts.push('Отдельные услуги разгрузки');
  if (extraMaterialRows.length > 0) secondarySummaryTextParts.push('Связанные услуги и допродажи');

  const secondaryTotalPieces = secondaryRows.filter(row => row.kind === 'material').reduce((sum, row) => sum + (row.pieces || 0), 0);
  if (secondaryTotalPieces > 0) {
    const lastPart = secondarySummaryTextParts[secondarySummaryTextParts.length - 1];
    // Внутри области слово склоняется («1 поддон)», «2 поддона)»), поэтому проверяем
    // корень, а не одну форму. Вне области условие остаётся базовым.
    const hasPalletsTail = isMskGazobetonScope
      ? /поддон\w*\)/.test(lastPart || '')
      : Boolean(lastPart && lastPart.includes('поддонов)'));
    if (hasPalletsTail) {
      secondarySummaryTextParts[secondarySummaryTextParts.length - 1] += ` | ${secondaryTotalPieces} шт.`;
    } else {
      secondarySummaryTextParts.push(`${secondaryTotalPieces} шт.`);
    }
  }

  const secondaryMaterialsForSummary = secondaryVolumes.some((value) => value > 0)
    ? gazMaterials.filter((_, index) => secondaryVolumes[index] > 0)
    : [];
  const secondaryVolumesForSummary = secondaryVolumes.filter((value) => value > 0);

  const secondaryBlock = {
    key: 'secondary',
    title: 'Остаток и отдельные услуги',
    subtitle: secondarySummaryTextParts.join(' | '),
    emptyText: 'Остаток и отдельные услуги в этом сценарии не требуются.',
    rows: secondaryRows,
    total: secondaryBlockTotal,
    ...(isMskGazobetonScope ? { gazCargoItems: buildGazCargoItems(secondaryRows, secondaryGazPallets, gazMaterials) } : {}),
    // Позиции, чьё паллетоместо посчитать нечем. Их читает buildGazVehicleLoadModel
    // и не объявляет такую машину полной, а свободное место в ней не обещает.
    // Поле добавляется только внутри области: вне её объект блока обязан совпадать
    // с 5442898 вплоть до состава ключей.
    ...(isMskGazobetonScope ? { unmeasuredCargoNames } : {}),
    priceSummaries: buildPriceSummaries({
      materials: secondaryMaterialsForSummary,
      volumes: secondaryVolumesForSummary,
      blockTotal: secondarySummaryTotal,
    }),
  };

  return {
    isGazobetonOffer: true,
    blocks: [mainBlock, secondaryBlock],
  };
};

export const buildGazCommercialProposalSendPayload = (offer, mode = 'full', { isMskGazobetonScope = false } = {}) => {
  if (mode !== 'partial') {
    return offer;
  }

  // Признак области передаётся дальше: без него блоки собрались бы базовым путём
  // и снова пересчитали бы число машин по одной норме — вариант «только полные
  // машины» показывал бы на машину больше, чем посчитал калькулятор.
  const proposal = buildGazCommercialProposalBlocks(offer, { isMskGazobetonScope });
  if (!proposal.isGazobetonOffer) {
    return offer;
  }

  const mainBlock = proposal.blocks.find((block) => block.key === 'main');
  if (!mainBlock) {
    return offer;
  }

  const sourceMaterials = Array.isArray(offer?.materials) ? offer.materials : [];
  const materialRows = mainBlock.rows.filter((row) => row.kind === 'material');
  const serviceRows = mainBlock.rows.filter((row) => row.kind === 'service');
  const deliveryRow = serviceRows[0] || null;

  const materials = materialRows.map((row) => {
    const sourceMaterial = sourceMaterials[row.originalIndex] || {};
    const sourceQuantity = isFiniteNumber(sourceMaterial.factVolume ?? sourceMaterial.quantity);
    const quantityRatio = sourceQuantity > 0 ? row.quantity / sourceQuantity : 0;
    const scalePalletField = (value) => {
      const num = isFiniteNumber(value);
      return num > 0 ? roundTo3(num * quantityRatio) : 0;
    };

    return {
      ...sourceMaterial,
      __originalIndex: row.originalIndex,
      quantity: row.quantity,
      factVolume: row.quantity,
      sum: row.sum,
      pricePerOne: row.pricePerOne,
      numberPallets: scalePalletField(sourceMaterial.numberPallets),
      occupiedPallets: scalePalletField(sourceMaterial.occupiedPallets),
      totalPallets: scalePalletField(sourceMaterial.totalPallets),
    };
  });

  // Разгрузка этого варианта заказа. Вариант и способ разгрузки выбираются
  // независимо, поэтому разгрузка считается для СВОЕГО состава машин: полные
  // шаланды без машины остатка. Число приходит готовым из расчёта доставки
  // (`fullVehiclesOnlyUnloading`) — своих формул и тарифов разгрузки на фронте нет,
  // как и своего счёта рейсов.
  //
  // Вне области ключа в расчёте нет, и разгрузка обнуляется, как в 5442898:
  // поведение базового пути не меняется.
  const sourceBreakdown = offer?.delivery?.deliveryBreakdown || {};
  const variantUnloading = isMskGazobetonScope && sourceBreakdown.fullVehiclesOnlyUnloading
    ? sourceBreakdown.fullVehiclesOnlyUnloading
    : null;
  const variantUnloadingCost = isFiniteNumber(variantUnloading?.unloadingCost);
  const variantCraneUnloadingCost = isFiniteNumber(variantUnloading?.craneUnloadingCost);
  const variantUnloadingTotal = variantUnloading
    ? roundTo3(variantUnloadingCost + variantCraneUnloadingCost)
    : 0;
  // Вне области сумма доставки варианта остаётся ровно прежней, без пересчёта.
  const variantDeliveryTotal = (sum) => (variantUnloadingTotal > 0 ? roundTo3(sum + variantUnloadingTotal) : sum);
  // Поддоны варианта — из его же строк материалов, уже пересчитанных выше.
  const variantPallets = roundTo3(materials.reduce(
    (sum, material) => sum + isFiniteNumber(material.numberPallets),
    0
  ));

  const delivery = deliveryRow && offer?.delivery && offer.delivery.name !== 'Самовывоз'
    ? {
      ...offer.delivery,
      name: deliveryRow.name,
      numberOfTrips: deliveryRow.quantity,
      costPerTrip: deliveryRow.pricePerOne,
      totalCost: variantDeliveryTotal(deliveryRow.sum),
      deliveryBreakdown: {
        ...sourceBreakdown,
        shalandaCount: deliveryRow.key === 'main-shalanda' ? deliveryRow.quantity : 0,
        fullShalandaCount: deliveryRow.key === 'main-shalanda' ? deliveryRow.quantity : 0,
        remainderPallets: 0,
        manipulatorTripCost: 0,
        unloadingCost: variantUnloadingCost,
        craneUnloadingCost: variantCraneUnloadingCost,
        // Способ разгрузки сохраняется: переключение варианта заказа его не
        // сбрасывает. Вне области он обнуляется, как в базовом коммите.
        unloadMethod: variantUnloading ? (sourceBreakdown.unloadMethod || '') : '',
        // Ключи ниже добавляются только внутри области: вне её состав объекта и
        // значения обязаны совпадать с 5442898.
        ...(variantUnloading ? {
          // Часы «встречи» пустого манипулятора — для состава этого варианта: по ним
          // карточка называет технику, приехавшую только разгружать.
          unloadingHours: isFiniteNumber(variantUnloading.unloadingHours),
          // Разгружаются только шаланды этого варианта, машины остатка в нём нет.
          shalandaPallets: variantPallets,
        } : {}),
        manipulatorGoesWithShalands: false,
        transportationCostWithoutUnloading: deliveryRow.sum,
        deliveryCost: deliveryRow.sum,
        totalCost: variantDeliveryTotal(deliveryRow.sum),
      },
    }
    : null;

  return {
    ...offer,
    materials,
    delivery,
  };
};

export default buildGazCommercialProposalBlocks;
